import { AsyncPipe } from "@angular/common";
import { Component, Input, OnChanges, SimpleChanges, inject, signal } from "@angular/core";
import { RiskDataService } from "@app/risk/services/RiskDataService";
import { CerrixButtonComponent } from "@app/shared/atoms/cerrix-button/cerrix-button.component";
import { CerrixLinkedItemTableComponent } from "@app/shared/organisms/cerrix-linked-item-table/cerrix-linked-item-table.component";
import { CerrixLinkItemsDialogComponent } from "@app/shared/organisms/cerrix-link-items-dialog/cerrix-link-items-dialog.component";
import { LinkableItem } from "@app/shared/organisms/cerrix-link-items-dialog/models/linkable-item";
import { CerrixDialogService } from "@app/shared/organisms/cerrix-dialog/cerrix-dialog-service.component";
import { LinkedRiskRowComponent } from "@app/assetmanagement/templates/linked-items/risks/linked-risk-row.component";
import {
    LinkedRiskTableDetails,
    LinkedRiskTableItem,
} from "@app/assetmanagement/templates/linked-items/risks/linked-risk-table.models";
import {
    TranslatePipe,
    injectTranslation,
    provideTranslationScope,
} from "@app/shared/i18n/translation-scope";
import { scope } from "@app/assetmanagement/i18n/scope";
import { Pages } from "@constants/pages/Pages";
import { ToastrService } from "ngx-toastr";
import { TabService } from "../../../../services/tabs/TabService";
import { Observable, catchError, firstValueFrom, map, of, startWith, throwError } from "rxjs";

type LinkedRisksState =
    | { status: "loading" | "error" }
    | { status: "loaded"; items: readonly LinkedRiskTableItem[] };

@Component({
    selector: "app-asset-linked-items-section",
    templateUrl: "./asset-linked-items-section.component.html",
    imports: [
        AsyncPipe,
        TranslatePipe,
        CerrixButtonComponent,
        CerrixLinkedItemTableComponent,
        CerrixLinkItemsDialogComponent,
        LinkedRiskRowComponent,
    ],
    viewProviders: [provideTranslationScope(scope)],
})
export class AssetLinkedItemsSectionComponent implements OnChanges {
    @Input({ required: true }) assetId: string;

    protected state$: Observable<LinkedRisksState>;
    protected readonly translationScope = scope;
    protected readonly linkDialogName = "asset-link-risks-dialog";
    protected readonly linkableItems = signal<readonly LinkableItem[]>([]);
    protected readonly linkedRiskIds = signal<readonly string[]>([]);

    private readonly riskDataService = inject(RiskDataService);
    private readonly tabService = inject(TabService);
    private readonly pages = inject(Pages);
    private readonly toastService = inject(ToastrService);
    private readonly dialogService = inject(CerrixDialogService);
    private readonly translation = injectTranslation(scope);
    private readonly unlinkingRiskIds = new Set<LinkedRiskTableItem["id"]>();
    private openingLinkDialog = false;
    private linking = false;

    protected readonly canLinkRisks$ = this.riskDataService.getAssetLinkPermissions().pipe(
        map((permissions) => permissions.canLinkRisks),
        catchError(() => of(false)),
    );

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.assetId) {
            this.loadRisks();
        }
    }

    protected loadRisks(): void {
        this.state$ = this.riskDataService.getLinkedRisks(this.assetId).pipe(
            map(
                (risks): LinkedRisksState => ({
                    status: "loaded",
                    items: risks.map((risk) => ({
                        id: risk.id,
                        identifier: risk.identifier,
                        name: risk.name,
                        grossRisk: risk.grossRisk,
                        grossRiskColor: risk.grossRiskColor,
                        netRisk: risk.netRisk,
                        netRiskColor: risk.netRiskColor,
                        canUnlink: risk.canUnlink,
                        confidential: !risk.canViewDetails,
                    })),
                }),
            ),
            startWith<LinkedRisksState>({ status: "loading" }),
            // The HTTP interceptor reports the error; retain a local failure state for retry.
            catchError(() => of<LinkedRisksState>({ status: "error" })),
        );
    }

    protected readonly loadRiskDetails = (
        item: LinkedRiskTableItem,
    ): Observable<LinkedRiskTableDetails> => {
        // A click on a confidential row does not expand it, but the keyboard toggle of the table still does.
        if (item.confidential) {
            void this.onConfidentialRiskClicked();
            return throwError(() => new Error("The risk is confidential."));
        }
        return this.riskDataService.getLinkedRiskDetails(this.assetId, item.id);
    };

    protected async onConfidentialRiskClicked(): Promise<void> {
        await this.translation.ready();
        this.toastService.info(this.translation.t("linkedItems.risks.confidentialDetails"));
    }

    protected onOpenLinkedRisk(item: LinkedRiskTableItem): void {
        this.openRiskDetailsTab(item.id);
    }

    protected onOpenLinkableRisk(item: LinkableItem): void {
        this.openRiskDetailsTab(item.id);
    }

    private openRiskDetailsTab(riskId: string): void {
        this.tabService.generateTab(this.pages.RiskDetail, riskId);
    }

    protected async onLinkRisks(linkedRisks: readonly LinkedRiskTableItem[]): Promise<void> {
        if (this.openingLinkDialog) {
            return;
        }
        this.openingLinkDialog = true;
        try {
            const linkableRisks = await firstValueFrom(
                this.riskDataService.getLinkableRisksForAssets(),
            );
            const linkedIds = linkedRisks.map((risk) => String(risk.id));
            const linkableIdSet = new Set(linkableRisks.map((risk) => risk.id));
            this.linkableItems.set([
                ...linkableRisks.map((risk) => ({
                    id: risk.id,
                    identifier: risk.identifier,
                    name: risk.name,
                    locked: false,
                })),
                // Linked risks the user may not link still show, but the user cannot unlink them.
                ...linkedRisks
                    .filter((risk) => !linkableIdSet.has(String(risk.id)))
                    .map((risk) => ({
                        id: String(risk.id),
                        identifier: risk.identifier,
                        name: risk.name,
                        locked: true,
                    })),
            ]);
            this.linkedRiskIds.set(linkedIds);
            this.dialogService.openDialog(this.linkDialogName);
        } catch {
            // The HTTP interceptor reports the error.
        } finally {
            this.openingLinkDialog = false;
        }
    }

    protected async onConfirmLinkRisks(selected: LinkableItem[]): Promise<void> {
        const riskIds = selected.map((item) => item.id);
        const linkedIds = new Set(this.linkedRiskIds());
        const unchanged =
            riskIds.length === linkedIds.size && riskIds.every((id) => linkedIds.has(id));
        if (unchanged || this.linking) {
            return;
        }
        this.linking = true;
        try {
            await firstValueFrom(this.riskDataService.setRisksLinkedToAsset(this.assetId, riskIds), {
                defaultValue: undefined,
            });
            await this.translation.ready();
            this.toastService.success(this.translation.t("linkedItems.risks.linksUpdated"));
            this.loadRisks();
        } catch {
            // The HTTP interceptor reports the error; the list stays as it was.
        } finally {
            this.linking = false;
        }
    }

    protected async onUnlinkRisk(item: LinkedRiskTableItem): Promise<void> {
        if (this.unlinkingRiskIds.has(item.id)) {
            return;
        }
        this.unlinkingRiskIds.add(item.id);
        try {
            await firstValueFrom(this.riskDataService.unlinkAsset(String(item.id), this.assetId));
            await this.translation.ready();
            this.toastService.success(this.translation.t("linkedItems.risks.unlinkCompleted"));
            this.loadRisks();
        } catch {
            // The HTTP interceptor reports the error; the list stays as it was.
        } finally {
            this.unlinkingRiskIds.delete(item.id);
        }
    }
}
